import React, { useState, useEffect, useRef } from "react";

export const LEAF_EMOJIS = ["🍁", "🥮", "🍂", "🏮"];

export interface FallingLeafProps {
  id?: number;
  left: number | string;
  delay?: number | string;
  size?: number;
  duration?: number;
  direction?: number;
  emoji?: string;
  isGameActive?: boolean;
  onComplete?: (id: number) => void;
  onCatch?: (id: number) => void;
}

function FallingLeaf({
  id,
  left,
  delay,
  size,
  duration,
  direction,
  emoji: customEmoji,
  isGameActive = true,
  onComplete,
  onCatch,
}: FallingLeafProps) {
  const leafRef = useRef<HTMLDivElement>(null);
  const [isFading, setIsFading] = useState(false);
  const lastCatchTimeRef = useRef<number>(0);

  const [emoji] = useState(
    () => customEmoji || LEAF_EMOJIS[Math.floor(Math.random() * LEAF_EMOJIS.length)]
  );

  // Chỉ cho phép bắt bánh trung thu 🥮 KHI GAME ĐANG BẬT (isGameActive === true)
  const isCatchable = isGameActive && emoji === "🥮";

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const finalDuration = duration ?? 8.5 + Math.random() * 10;
    const finalSize = size ?? 14 + Math.random() * 18;
    const finalDirection = direction ?? (Math.random() - 0.5) * 160;
    const isMooncake = emoji === "🥮";
    const initRotate = isMooncake
      ? (Math.random() - 0.5) * 20
      : (Math.random() - 0.5) * 60;
    const endRotate = isMooncake
      ? initRotate + (Math.random() - 0.5) * 25
      : initRotate + (Math.random() - 0.5) * 120;

    if (leafRef.current) {
      leafRef.current.style.left = typeof left === "number" ? `${left}%` : left;
      leafRef.current.style.animationDuration = `${finalDuration}s`;
      leafRef.current.style.fontSize = `${finalSize}px`;
      if (delay !== undefined) {
        leafRef.current.style.animationDelay = typeof delay === "number" ? `${delay}s` : delay;
      }
      leafRef.current.style.setProperty("--direction", `${finalDirection}px`);
      leafRef.current.style.setProperty("--init-rotate", `${initRotate}deg`);
      leafRef.current.style.setProperty("--end-rotate", `${endRotate}deg`);
    }

    const safetyTimer = setTimeout(() => {
      if (id !== undefined && onCompleteRef.current && !isFading) {
        onCompleteRef.current(id);
      }
    }, (finalDuration + 1) * 1000);

    return () => {
      clearTimeout(safetyTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnimationEnd = () => {
    if (id !== undefined && onCompleteRef.current && !isFading) {
      onCompleteRef.current(id);
    }
  };

  const handleTriggerCatch = (e: React.SyntheticEvent) => {
    // Nếu game chưa bật hoặc không phải bánh trung thu thì click không có hiệu lực
    if (!isCatchable) {
      return;
    }

    e.stopPropagation();
    const now = Date.now();
    if (now - lastCatchTimeRef.current < 250) return;
    lastCatchTimeRef.current = now;

    if (isFading) return;
    setIsFading(true);

    if (id !== undefined) {
      if (onCatch) onCatch(id);
      setTimeout(() => {
        if (onComplete) onComplete(id);
      }, 300);
    }
  };

  return (
    <div
      ref={leafRef}
      className={`falling-leaf ${isCatchable ? "is-catchable cursor-pointer" : "is-uncatchable"} ${isFading ? "is-fading" : ""}`}
      onAnimationEnd={handleAnimationEnd}
      onPointerDown={handleTriggerCatch}
    >
      <div className="leaf-content">{emoji}</div>
    </div>
  );
}

export default FallingLeaf;
